import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Send, Mic, Square, Volume2, Trash2, Loader2, ShieldCheck, Unlock, Plus, Image as ImageIcon, Presentation, Wand2, Palette, Link2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { AiMedia } from './AiMedia';
import { AiActionPreview } from './AiActionPreview';

const API = process.env.REACT_APP_BACKEND_URL;
const cfg = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const readError = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string' && detail) return detail;
  if (Array.isArray(detail)) return detail.map(item => item?.msg || '').filter(Boolean).join('; ') || fallback;
  if (!error?.response && typeof error?.message === 'string' && error.message && error.message !== 'Network Error') return error.message;
  return fallback;
};

export const AiChat = ({ sessionId, onInsert }) => {
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState('grounded');
  const [recording, setRecording] = useState(false);
  const [speakingId, setSpeakingId] = useState('');
  const [action, setAction] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [targets, setTargets] = useState({ sheets: [], email_ready: false });
  const [sessionLinks, setSessionLinks] = useState([]);
  const [houseStyle, setHouseStyle] = useState(true);
  const recorder = useRef(null);
  const chunks = useRef([]);
  const bottom = useRef(null);
  const controller = useRef(null);

  useEffect(() => {
    axios.get(`${API}/api/kb/ai/chat/${sessionId}`, cfg())
      .then(res => { setMessages(res.data.messages || []); setSessionLinks(res.data.links || []); })
      .catch(() => setMessages([]));
    return () => controller.current?.abort();
  }, [sessionId]);

  useEffect(() => {
    axios.get(`${API}/api/kb/ai/actions/targets`, cfg())
      .then(res => setTargets(res.data))
      .catch(() => {});
  }, []);

  useEffect(() => { bottom.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, busy]);

  const send = async text => {
    const question = (text ?? draft).trim();
    if (!question || busy) return;
    setDraft('');
    let route = 'chat';
    setBusy(true);
    try {
      const picked = await axios.post(`${API}/api/kb/ai/route`, { message: question }, cfg());
      route = picked.data.route || 'chat';
    } catch {
      route = 'chat';
    }
    setBusy(false);
    if (route !== 'chat') {
      await tool(route === 'action' ? 'plan' : route, question);
      return;
    }
    await streamChat(question);
  };

  const streamChat = async question => {
    setMessages(prev => [...prev, { id: `me-${Date.now()}`, role: 'user', text: question }]);
    setBusy(true);
    controller.current = new AbortController();
    let reply = '';
    const replyId = `ai-${Date.now()}`;
    setMessages(prev => [...prev, { id: replyId, role: 'assistant', text: '', mode }]);
    try {
      const response = await fetch(`${API}/api/kb/ai/chat`, {
        method: 'POST', signal: controller.current.signal,
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, message: question, mode, use_knowledge_base: true })
      });
      if (!response.ok || !response.body) throw new Error('chat failed');
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop() || '';
        for (const part of parts) {
          if (!part.startsWith('data: ')) continue;
          const event = JSON.parse(part.slice(6));
          if (event.type === 'delta') {
            reply += event.text;
            setMessages(prev => prev.map(item => (item.id === replyId ? { ...item, text: reply } : item)));
          } else if (event.type === 'start') {
            setMessages(prev => prev.map(item => (item.id === replyId ? { ...item, sources: event.sources } : item)));
          } else if (event.type === 'error') {
            toast.error(event.message);
            setMessages(prev => prev.filter(item => item.id !== replyId));
          }
        }
      }
    } catch (error) {
      if (error.name !== 'AbortError') toast.error('Officium AI could not reply. Please try again.');
      setMessages(prev => prev.filter(item => item.id !== replyId || item.text));
    } finally {
      setBusy(false);
      axios.get(`${API}/api/kb/ai/chat/${sessionId}`, cfg())
        .then(res => setSessionLinks(res.data.links || []))
        .catch(() => {});
    }
  };

  const waitForDeck = async jobId => {
    for (let attempt = 0; attempt < 140; attempt += 1) {
      await new Promise(resolve => setTimeout(resolve, 3000));
      const res = await axios.get(`${API}/api/kb/ai/pptx/${jobId}`, cfg());
      if (res.data.status === 'done') return res.data.message;
      if (res.data.status === 'error') throw new Error(res.data.error || 'The slide pack could not be built.');
    }
    throw new Error('The slide pack is taking unusually long. Check back in a moment.');
  };

  const tool = async (kind, override) => {
    const text = (override ?? draft).trim();
    if (!text) {
      toast.info('Type what you want first, then choose a tool');
      return;
    }
    if (kind === 'image' && text.length > 2000) {
      toast.error('That image description is too long - keep it under 2000 characters');
      return;
    }
    if (kind === 'pptx' && text.length > 4000) {
      toast.error('That slide pack brief is too long - keep it under 4000 characters');
      return;
    }
    setDraft('');
    setBusy(true);
    const waitingId = `wait-${Date.now()}`;
    const waiting = { image: 'Creating that image…', pptx: 'Building that slide pack…', plan: 'Working out what to do…' }[kind];
    setMessages(prev => [...prev, { id: `me-${Date.now()}`, role: 'user', text },
      { id: waitingId, role: 'assistant', text: waiting }]);
    try {
      if (kind === 'plan') {
        const res = await axios.post(`${API}/api/kb/ai/actions/plan`, { session_id: sessionId, message: text }, cfg());
        setMessages(prev => prev.filter(item => item.id !== waitingId));
        if (res.data.kind === 'none') {
          setMessages(prev => [...prev, { id: `ai-${Date.now()}`, role: 'assistant', text: res.data.summary }]);
        } else {
          setAction(res.data);
        }
      } else if (kind === 'image') {
        const res = await axios.post(`${API}/api/kb/ai/image`,
          { session_id: sessionId, prompt: text, house_style: houseStyle }, cfg());
        setMessages(prev => [...prev.filter(item => item.id !== waitingId), res.data.message]);
      } else {
        const started = await axios.post(`${API}/api/kb/ai/pptx`,
          { session_id: sessionId, topic: text, use_knowledge_base: true }, cfg());
        const finished = await waitForDeck(started.data.job_id);
        setMessages(prev => [...prev.filter(item => item.id !== waitingId), finished]);
      }
    } catch (error) {
      setMessages(prev => prev.filter(item => item.id !== waitingId));
      toast.error(readError(error, 'Officium AI could not finish that. Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  const confirmAction = async (payload) => {
    setActionBusy(true);
    try {
      const res = await axios.post(`${API}/api/kb/ai/actions/${action.id}/confirm`, { payload }, cfg());
      const result = res.data.result || {};
      const done = result.rows_added !== undefined ? `Added ${result.rows_added} row(s) to ${result.sheet_name || 'the sheet'}`
        : result.provider_id ? `Email sent to ${(result.to || []).join(', ')}`
          : result.interview_id ? `Interview booked for ${result.scheduled_date} at ${result.scheduled_time}`
            : result.page_id ? `Draft page "${result.title}" created` : 'Done';
      setMessages(prev => [...prev, { id: `act-${Date.now()}`, role: 'assistant', text: done }]);
      toast.success(done);
      setAction(null);
    } catch (error) {
      toast.error(readError(error, 'That action could not be completed.'));
    } finally {
      setActionBusy(false);
    }
  };

  const discardAction = async () => {
    await axios.delete(`${API}/api/kb/ai/actions/${action.id}`, cfg()).catch(() => {});
    setAction(null);
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      chunks.current = [];
      const media = new MediaRecorder(stream);
      media.ondataavailable = event => chunks.current.push(event.data);
      media.onstop = async () => {
        stream.getTracks().forEach(track => track.stop());
        const blob = new Blob(chunks.current, { type: 'audio/webm' });
        if (blob.size < 1200) { toast.error('That recording was too short'); return; }
        const body = new FormData();
        body.append('file', blob, 'speech.webm');
        setBusy(true);
        try {
          const res = await axios.post(`${API}/api/kb/ai/transcribe`, body, cfg());
          setBusy(false);
          await send(res.data.text);
        } catch (error) {
          setBusy(false);
          toast.error(readError(error, 'Could not understand that recording'));
        }
      };
      media.start();
      recorder.current = media;
      setRecording(true);
    } catch {
      toast.error('Microphone access was blocked by your browser');
    }
  };

  const stopRecording = () => {
    recorder.current?.stop();
    recorder.current = null;
    setRecording(false);
  };

  const speak = async message => {
    setSpeakingId(message.id);
    try {
      const res = await axios.post(`${API}/api/kb/ai/speak`, { text: message.text.slice(0, 3900), voice: 'alloy' },
        { ...cfg(), responseType: 'blob' });
      const audio = new Audio(URL.createObjectURL(res.data));
      audio.onended = () => setSpeakingId('');
      await audio.play();
    } catch (error) {
      setSpeakingId('');
      toast.error(readError(error, 'Could not read that out'));
    }
  };

  const clear = async () => {
    await axios.delete(`${API}/api/kb/ai/chat/${sessionId}`, cfg()).catch(() => {});
    setMessages([]);
    setSessionLinks([]);
  };

  return (
    <div className="flex h-full flex-col gap-3" data-testid="kb-ai-chat">
      <div className="flex items-center justify-between gap-2">
        <div className="flex rounded-full bg-slate-100 p-0.5">
          {[['grounded', 'Grounded', ShieldCheck], ['open', 'Open mode', Unlock]].map(([id, label, Icon]) => (
            <button key={id} type="button" onClick={() => setMode(id)} data-testid={`kb-ai-chat-mode-${id}`}
              className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] transition-colors ${mode === id ? 'bg-white text-indigo-700 shadow-sm' : 'text-slate-500'}`}>
              <Icon className="h-3 w-3" />{label}
            </button>
          ))}
        </div>
        <Button variant="ghost" size="sm" className="h-7 px-2 text-slate-500" onClick={clear} data-testid="kb-ai-chat-clear">
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>

      {mode === 'open' && (
        <p className="rounded-lg bg-amber-50 px-2.5 py-1.5 text-[11px] text-amber-800" data-testid="kb-ai-chat-open-note">
          Open mode: answers anything using general knowledge, so facts may be unverified. Page access rules still apply.
        </p>
      )}

      <div className="flex-1 min-h-0 space-y-2.5 overflow-y-auto pr-1" data-testid="kb-ai-chat-thread">
        {messages.length === 0 && !busy && (
          <p className="text-xs text-slate-400">Ask anything, or hold the microphone and talk.</p>
        )}
        {messages.map(message => (
          <div key={message.id} className={message.role === 'user' ? 'flex justify-end' : 'flex justify-start'}>
            <div className={`max-w-[88%] rounded-2xl px-3 py-2 text-xs leading-relaxed whitespace-pre-wrap [overflow-wrap:anywhere] ${
              message.role === 'user' ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-700'}`}
              data-testid={`kb-ai-chat-${message.role}`}>
              {message.text || (busy ? 'Thinking…' : '')}
              {message.media && <AiMedia media={message.media} onAddToPage={onInsert ? html => onInsert(html, { html: true }) : null} />}
              {message.role === 'assistant' && message.text && (
                <div className="mt-1.5 flex items-center gap-2">
                  <button onClick={() => speak(message)} className="text-[11px] text-slate-500 hover:text-indigo-600 inline-flex items-center gap-1"
                    data-testid="kb-ai-chat-speak">
                    {speakingId === message.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Volume2 className="h-3 w-3" />}Listen
                  </button>
                  {onInsert && !message.media && (
                    <button onClick={() => onInsert(message.text)} className="text-[11px] text-slate-500 hover:text-indigo-600 inline-flex items-center gap-1"
                      data-testid="kb-ai-chat-insert">
                      <Plus className="h-3 w-3" />Add to page
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
        <div ref={bottom} />
      </div>

      {action && (
        <AiActionPreview action={action} sheets={targets.sheets} busy={actionBusy}
          onConfirm={confirmAction} onDiscard={discardAction} />
      )}

      {sessionLinks.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5" data-testid="kb-ai-session-links">
          <span className="text-[10px] uppercase tracking-wide text-slate-400">Read in this chat</span>
          {sessionLinks.map(link => (
            <a key={link.url} href={link.url} target="_blank" rel="noreferrer" title={link.url}
              data-testid="kb-ai-session-link"
              className="inline-flex max-w-[170px] items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600 hover:text-indigo-700">
              <Link2 className="h-3 w-3 shrink-0" /><span className="truncate">{link.label}</span>
            </a>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-1.5">
        <button type="button" onClick={() => setHouseStyle(!houseStyle)} data-testid="kb-ai-house-style"
          title="Draw images in Officium's colours and fonts"
          className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] transition-colors ${houseStyle ? 'border-indigo-300 bg-indigo-50 text-indigo-700' : 'border-slate-200 text-slate-500 hover:border-indigo-300'}`}>
          <Palette className="h-3 w-3" />House style
        </button>
        {[['image', 'Image', ImageIcon], ['pptx', 'PPT pack', Presentation], ['plan', 'Do it for me', Wand2]].map(([id, label, Icon]) => (
          <button key={id} type="button" onClick={() => tool(id)} disabled={busy}
            data-testid={`kb-ai-tool-${id}`}
            className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2.5 py-1 text-[11px] text-slate-600 transition-colors hover:border-indigo-300 hover:text-indigo-700 disabled:opacity-50">
            <Icon className="h-3 w-3" />{label}
          </button>
        ))}
      </div>

      <div className="flex items-end gap-2">
        <Textarea rows={2} value={draft} onChange={event => setDraft(event.target.value)} disabled={busy}
          placeholder={recording ? 'Listening…' : 'Message Officium AI'} className="text-xs" data-testid="kb-ai-chat-input"
          onKeyDown={event => { if (event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); send(); } }} />
        <div className="flex flex-col gap-1.5">
          <Button size="sm" className="h-8 bg-indigo-600 hover:bg-indigo-700" onClick={() => send()} disabled={busy || !draft.trim()}
            data-testid="kb-ai-chat-send"><Send className="h-3.5 w-3.5" /></Button>
          <Button size="sm" variant={recording ? 'destructive' : 'outline'} className="h-8"
            onClick={recording ? stopRecording : startRecording} data-testid="kb-ai-chat-mic">
            {recording ? <Square className="h-3.5 w-3.5" /> : <Mic className="h-3.5 w-3.5" />}
          </Button>
        </div>
      </div>
    </div>
  );
};
